import React, { useState } from "react";
import { Head, useForm } from "@inertiajs/react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Eye, EyeOff, Lock, User, Mail } from "lucide-react";

interface Props {
  user: {
    email: string;
    name?: string;
  };
  invitation_token: string;
}

export default function InvitationEdit({ user, invitation_token }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);

  const { data, setData, put, processing, errors } = useForm({
    invitation_token: invitation_token,
    password: "",
    password_confirmation: "",
    name: user.name || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put("/users/invitation", {
      onSuccess: () => {
        // Redirect will be handled by the controller
      },
    });
  };

  return (
    <>
      <Head title="Complete Your Registration - CFS" />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mb-2">
              Welcome to Resolute.
            </h1>
            <p className="text-gray-600">
              Complete your registration to get started
            </p>
          </div>

          <Card className="">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold text-center">
                Set Your Password
              </CardTitle>
              <CardDescription className="text-center">
                You've been invited to join our platform. Please set your
                password to complete your account setup.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Display */}
                <div>
                  <Label
                    htmlFor="email"
                    className="flex items-center text-sm font-medium text-gray-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Address
                  </Label>
                  <div className="mt-1 flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className="text-gray-600">{user.email}</span>
                  </div>
                </div>

                {/* Name Field */}
                <div>
                  <Label
                    htmlFor="name"
                    className="flex items-center text-sm font-medium text-gray-700"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    className="mt-1"
                    placeholder="Enter your full name"
                    required
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <Label
                    htmlFor="password"
                    className="flex items-center text-sm font-medium text-gray-700"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Password
                  </Label>
                  <div className="mt-1 relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={data.password}
                      onChange={(e) => setData("password", e.target.value)}
                      className="pr-10"
                      placeholder="Create a strong password"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Password must be at least 6 characters long
                  </p>
                </div>

                {/* Password Confirmation Field */}
                <div>
                  <Label
                    htmlFor="password_confirmation"
                    className="flex items-center text-sm font-medium text-gray-700"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Confirm Password
                  </Label>
                  <div className="mt-1 relative">
                    <Input
                      id="password_confirmation"
                      type={showPasswordConfirmation ? "text" : "password"}
                      value={data.password_confirmation}
                      onChange={(e) =>
                        setData("password_confirmation", e.target.value)
                      }
                      className="pr-10"
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() =>
                        setShowPasswordConfirmation(!showPasswordConfirmation)
                      }
                    >
                      {showPasswordConfirmation ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password_confirmation && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password_confirmation}
                    </p>
                  )}
                </div>

                {/* General Errors */}
                {errors.invitation_token && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Invitation Error
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{errors.invitation_token}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-b from-blue-500 to-blue-600  text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform cursor-pointer "
                  disabled={processing}
                >
                  {processing ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Setting up your account...
                    </div>
                  ) : (
                    "Complete Registration"
                  )}
                </Button>
              </form>

              {/* Footer */}
              {/* <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center">
                  By completing your registration, you agree to our terms of
                  service and privacy policy.
                </p>
              </div> */}
            </CardContent>
          </Card>

          {/* Additional Info */}
          {/* <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help? Contact us at{" "}
              <a
                href="mailto:support@mailkit.deliverabilityfirst.com"
                className="text-blue-600 hover:text-blue-800"
              >
                support@mailkit.deliverabilityfirst.com
              </a>
            </p>
          </div> */}
        </div>
      </div>
    </>
  );
}
